import React from "react";
import { createPortal } from "react-dom";
import {
  MdAttachFile,
  MdClose,
  MdDeleteOutline,
  MdDownload,
  MdError,
  MdImage,
  MdInsertDriveFile,
  MdMic,
  MdNotifications,
  MdNotificationsOff,
  MdPause,
  MdPictureAsPdf,
  MdPlayArrow,
  MdRefresh,
  MdSchedule,
  MdSend,
  MdStop,
  MdVideocam,
} from "react-icons/md";
import { useDirection } from "hooks/useDirection";
import { useFirebaseCourseChat, type FirebaseConnectionMode } from "hooks/useFirebaseCourseChat";
import {
  courseChatService,
  type CourseChatMessage,
  type CourseChatScope,
  type CourseChatSettings,
} from "services/courseChatService";
import { getAttachmentUrl, invalidateAttachmentUrl } from "services/attachmentUrlCache";
import {
  getRememberedFirebaseUid,
  loadCachedMessages,
  rememberFirebaseUid,
  saveCachedMessages,
} from "services/chatMessageCache";
import { registerWebPush, setActiveChatCourseId } from "services/webPush";
import {
  fileExtensionLabel,
  formatFileSize,
  isPdfAttachment,
  isVideoAttachment,
  makeClientMessageId,
  mergeChatMessages,
} from "utils/chatAttachment";
import { Skeleton } from "components/ui/Skeleton";
import ConfirmDialog from "components/ui/ConfirmDialog";
import UserAvatar from "components/ui/UserAvatar";
import { ChatVideoPreviewModal } from "components/chat/ChatVideoPreviewModal";
import { toastError, toastSuccess } from "lib/toast";
import { getChatApiErrorMessage } from "utils/chatApiError";

type CourseChatPanelProps = {
  courseId: number | string;
  scope: CourseChatScope;
  canMute?: boolean;
  composerDisabled?: boolean;
};

const HEARTBEAT_INTERVAL_MS = 45_000;

function scopeRole(scope: CourseChatScope): string {
  return scope === "admin" ? "admin" : scope;
}

function roleBubbleClass(role: string, isMine: boolean): string {
  if (isMine) return "bg-brand-500 text-white";
  if (role === "admin" || role === "moderator") return "bg-purple-100 text-purple-900 dark:bg-purple-500/20 dark:text-purple-100";
  if (role === "instructor") return "bg-emerald-100 text-emerald-900 dark:bg-emerald-500/20 dark:text-emerald-100";
  return "bg-gray-100 text-gray-900 dark:bg-white/10 dark:text-gray-100";
}

function formatTime(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
}

function formatVoiceDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem.toString().padStart(2, "0")}`;
}

function ChatSenderAvatar({
  name,
  url,
}: {
  name: string | null;
  url: string | null;
}) {
  return (
    <div
      className="h-8 w-8 shrink-0 overflow-hidden rounded-full ring-1 ring-black/5 dark:ring-white/10"
      title={name ?? undefined}
    >
      <UserAvatar src={url} alt={name ?? ""} className="h-full w-full object-cover" />
    </div>
  );
}

const VOICE_WAVE_BARS = 32;

function buildVoiceWaveform(seed: string): number[] {
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  }
  const bars: number[] = [];
  for (let i = 0; i < VOICE_WAVE_BARS; i += 1) {
    hash = (hash * 1664525 + 1013904223) >>> 0;
    const n = (hash % 1000) / 1000;
    // WhatsApp-like shape: quieter edges, lively middle
    const envelope = 0.35 + 0.65 * Math.sin((Math.PI * (i + 0.5)) / VOICE_WAVE_BARS);
    bars.push(0.22 + n * 0.78 * envelope);
  }
  return bars;
}

let activeVoiceStop: (() => void) | null = null;

function VoiceBubble({
  url,
  durationSeconds,
  isArabic,
  isMine = true,
}: {
  url: string;
  durationSeconds?: number | null;
  isArabic: boolean;
  isMine?: boolean;
}) {
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const waveRef = React.useRef<HTMLDivElement | null>(null);
  const [playing, setPlaying] = React.useState(false);
  const [current, setCurrent] = React.useState(0);
  const [loadedDuration, setLoadedDuration] = React.useState(0);
  const bars = React.useMemo(() => buildVoiceWaveform(url), [url]);

  const total = Math.max(durationSeconds ?? 0, loadedDuration, 0);
  const progress = total > 0 ? Math.min(1, current / total) : 0;

  const stop = React.useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
    setPlaying(false);
    setCurrent(0);
  }, []);

  React.useEffect(() => {
    const audio = new Audio(url);
    audio.preload = "metadata";
    audioRef.current = audio;

    const onTime = () => setCurrent(audio.currentTime);
    const onMeta = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setLoadedDuration(audio.duration);
      }
    };
    const onEnded = () => {
      setPlaying(false);
      setCurrent(0);
      if (activeVoiceStop === stop) activeVoiceStop = null;
    };

    audio.addEventListener("timeupdate", onTime);
    audio.addEventListener("loadedmetadata", onMeta);
    audio.addEventListener("ended", onEnded);

    return () => {
      if (activeVoiceStop === stop) activeVoiceStop = null;
      audio.pause();
      audio.removeEventListener("timeupdate", onTime);
      audio.removeEventListener("loadedmetadata", onMeta);
      audio.removeEventListener("ended", onEnded);
      audioRef.current = null;
    };
  }, [url, stop]);

  const toggle = async () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (playing) {
      audio.pause();
      setPlaying(false);
      if (activeVoiceStop === stop) activeVoiceStop = null;
      return;
    }

    if (activeVoiceStop && activeVoiceStop !== stop) {
      activeVoiceStop();
    }
    activeVoiceStop = stop;

    try {
      await audio.play();
      setPlaying(true);
    } catch {
      setPlaying(false);
      if (activeVoiceStop === stop) activeVoiceStop = null;
    }
  };

  const seekFromClientX = (clientX: number) => {
    const audio = audioRef.current;
    const el = waveRef.current;
    if (!audio || !el || total <= 0) return;
    const rect = el.getBoundingClientRect();
    const raw = (clientX - rect.left) / Math.max(rect.width, 1);
    const ratio = Math.max(0, Math.min(1, isArabic ? 1 - raw : raw));
    audio.currentTime = ratio * total;
    setCurrent(audio.currentTime);
  };

  const label =
    playing || current > 0
      ? formatVoiceDuration(current)
      : formatVoiceDuration(total || durationSeconds || 0);

  const barPlayed = isMine ? "bg-white" : "bg-emerald-700 dark:bg-emerald-300";
  const barIdle = isMine ? "bg-white/40" : "bg-emerald-700/35 dark:bg-emerald-300/35";
  const thumbClass = isMine
    ? "bg-white ring-2 ring-emerald-700/30"
    : "bg-emerald-700 ring-2 ring-white dark:bg-emerald-300";
  const playBtnClass = isMine
    ? "bg-white text-emerald-800 hover:bg-white/95"
    : "bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-500";

  return (
    <div
      dir={isArabic ? "rtl" : "ltr"}
      className="flex w-[252px] max-w-full items-center gap-2.5 py-0.5"
    >
      <button
        type="button"
        onClick={() => void toggle()}
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full shadow-sm transition ${playBtnClass}`}
        aria-label={playing ? (isArabic ? "إيقاف" : "Pause") : isArabic ? "تشغيل" : "Play"}
      >
        {playing ? (
          <MdPause className="h-6 w-6" />
        ) : (
          <MdPlayArrow className="h-7 w-7 translate-x-px" />
        )}
      </button>

      <div className="min-w-0 flex-1">
        <div
          ref={waveRef}
          role="slider"
          tabIndex={0}
          aria-valuemin={0}
          aria-valuemax={Math.round(total)}
          aria-valuenow={Math.round(current)}
          aria-label={isArabic ? "موجة التسجيل" : "Voice waveform"}
          onClick={(e) => seekFromClientX(e.clientX)}
          className="relative flex h-9 w-full cursor-pointer items-center gap-[2px]"
        >
          {bars.map((height, index) => {
            const barProgress = (index + 0.5) / bars.length;
            const played = progress >= barProgress;
            const px = Math.max(6, Math.round(4 + height * 28));
            return (
              <span
                key={index}
                className={`min-w-[2px] flex-1 rounded-full ${played ? barPlayed : barIdle}`}
                style={{ height: `${px}px` }}
              />
            );
          })}
          <span
            className={`pointer-events-none absolute top-1/2 z-[1] h-3.5 w-3.5 -translate-y-1/2 rounded-full shadow ${thumbClass}`}
            style={
              isArabic
                ? { right: `calc(${progress * 100}% - 7px)` }
                : { left: `calc(${progress * 100}% - 7px)` }
            }
          />
        </div>
        <div className="mt-1 flex items-center justify-between gap-2 px-0.5">
          <span className="text-[11px] font-semibold tabular-nums leading-none opacity-95">
            {label}
          </span>
          <MdMic className="h-3.5 w-3.5 opacity-80" aria-hidden />
        </div>
      </div>
    </div>
  );
}

function PdfPreviewModal({
  url,
  name,
  isArabic,
  onClose,
}: {
  url: string;
  name: string | null;
  isArabic: boolean;
  onClose: () => void;
}) {
  const [embedFailed, setEmbedFailed] = React.useState(false);

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[1200] flex items-center justify-center bg-black/70 p-4"
      onClick={onClose}
    >
      <div
        className="flex h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-navy-800"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="flex items-center justify-between gap-2 border-b border-gray-100 px-4 py-3 dark:border-white/10">
          <div className="flex min-w-0 items-center gap-2">
            <MdPictureAsPdf className="h-5 w-5 shrink-0 text-red-500" />
            <span className="truncate text-sm font-semibold text-gray-800 dark:text-white">
              {name ?? (isArabic ? "مستند PDF" : "PDF document")}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="rounded-lg bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
            >
              {isArabic ? "فتح في تبويب" : "Open in tab"}
            </a>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-500 hover:bg-gray-100 dark:hover:bg-white/10"
              aria-label={isArabic ? "إغلاق" : "Close"}
            >
              <MdClose className="h-5 w-5" />
            </button>
          </div>
        </div>
        <div className="min-h-0 flex-1 bg-gray-50 dark:bg-navy-900">
          {embedFailed ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 p-6 text-center">
              <MdPictureAsPdf className="h-12 w-12 text-red-400" />
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {isArabic
                  ? "تعذّر عرض الملف هنا. افتحه في تبويب جديد."
                  : "Preview is unavailable here. Open it in a new tab."}
              </p>
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className="rounded-lg bg-brand-500 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-600"
              >
                {isArabic ? "فتح PDF" : "Open PDF"}
              </a>
            </div>
          ) : (
            <iframe
              title={name ?? "PDF"}
              src={url}
              className="h-full w-full"
              onError={() => setEmbedFailed(true)}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

function StatusIndicator({ status, isArabic }: { status?: CourseChatMessage["status"]; isArabic: boolean }) {
  if (!status || status === "sent") return null;
  if (status === "uploading" || status === "sending") {
    return (
      <MdSchedule
        className="h-3 w-3 opacity-70"
        title={isArabic ? "جاري الإرسال" : "Sending"}
      />
    );
  }
  if (status === "failed") {
    return <MdError className="h-3 w-3 text-red-400" title={isArabic ? "فشل الإرسال" : "Failed"} />;
  }
  return null;
}

function MessageBubble({
  message,
  isArabic,
  scope,
  courseId,
  canModerateDelete,
  onRetry,
  onDeletePending,
  onRequestDelete,
}: {
  message: CourseChatMessage;
  isArabic: boolean;
  scope: CourseChatScope;
  courseId: number | string;
  canModerateDelete: boolean;
  onRetry: (m: CourseChatMessage) => void;
  onDeletePending: (m: CourseChatMessage) => void;
  onRequestDelete: (m: CourseChatMessage) => void;
}) {
  const align = message.is_mine ? (isArabic ? "items-start" : "items-end") : (isArabic ? "items-end" : "items-start");
  const [attachmentUrl, setAttachmentUrl] = React.useState<string | null>(
    message.attachment?.url ?? message.local_preview_url ?? null
  );
  const [pdfOpen, setPdfOpen] = React.useState(false);
  const [videoOpen, setVideoOpen] = React.useState(false);
  const isLocal = message.local === true;
  const isDeleted = Boolean(message.is_deleted);
  const isPdf = isPdfAttachment(message);
  const isVideo = isVideoAttachment(message);
  const showModerateDelete =
    canModerateDelete &&
    !isDeleted &&
    !isLocal &&
    message.status !== "failed" &&
    message.message_type !== "system";

  React.useEffect(() => {
    let cancelled = false;
    // Local optimistic messages use their own preview URL; never resolve from server.
    if (isLocal) {
      setAttachmentUrl(message.attachment?.url ?? message.local_preview_url ?? null);
      return;
    }
    if (isDeleted) {
      setAttachmentUrl(null);
      return;
    }
    const needsUrl =
      message.attachment &&
      !message.attachment.url &&
      (message.attachment.path || message.message_type !== "text");

    if (!needsUrl) {
      setAttachmentUrl(message.attachment?.url ?? null);
      return;
    }

    void getAttachmentUrl(scope, courseId, message.id)
      .then((url) => {
        if (!cancelled) setAttachmentUrl(url);
      })
      .catch(() => {
        if (!cancelled) setAttachmentUrl(null);
      });

    return () => {
      cancelled = true;
    };
  }, [
    courseId,
    message.attachment,
    message.id,
    message.message_type,
    message.local_preview_url,
    scope,
    isLocal,
    isDeleted,
  ]);

  const openPdf = async () => {
    if (isLocal || isDeleted) return;
    try {
      const url = attachmentUrl ?? (await getAttachmentUrl(scope, courseId, message.id));
      setAttachmentUrl(url);
      setPdfOpen(true);
    } catch {
      toastError(isArabic ? "تعذّر فتح الملف" : "Failed to open file");
    }
  };

  const openVideo = async () => {
    if (isLocal || isDeleted) return;
    try {
      const url = attachmentUrl ?? (await getAttachmentUrl(scope, courseId, message.id));
      setAttachmentUrl(url);
      setVideoOpen(true);
    } catch {
      try {
        invalidateAttachmentUrl(scope, courseId, message.id);
        const url = await getAttachmentUrl(scope, courseId, message.id, true);
        setAttachmentUrl(url);
        setVideoOpen(true);
      } catch {
        toastError(isArabic ? "تعذّر فتح الفيديو" : "Failed to open video");
      }
    }
  };

  const downloadAttachment = async () => {
    if (isDeleted) return;
    try {
      const url = attachmentUrl ?? (await getAttachmentUrl(scope, courseId, message.id));
      window.open(url, "_blank", "noreferrer");
    } catch {
      // Signed URL may have expired — refresh once.
      try {
        invalidateAttachmentUrl(scope, courseId, message.id);
        const url = await getAttachmentUrl(scope, courseId, message.id, true);
        window.open(url, "_blank", "noreferrer");
      } catch {
        toastError(isArabic ? "تعذّر التحميل" : "Download failed");
      }
    }
  };

  if (isDeleted) {
    return (
      <div
        className={`flex w-full items-end gap-2 ${
          message.is_mine
            ? isArabic
              ? "justify-start"
              : "justify-end"
            : isArabic
              ? "justify-end"
              : "justify-start"
        }`}
      >
        {!message.is_mine && !isArabic ? (
          <ChatSenderAvatar name={message.sender.name} url={message.sender.avatar_url} />
        ) : null}
        <div className={`flex max-w-[85%] flex-col gap-1 ${align}`}>
          {!message.is_mine ? (
            <span className="px-1 text-xs font-medium text-gray-500 dark:text-gray-400">
              {message.sender.name ?? message.sender.role}
            </span>
          ) : null}
          <div
            className={`rounded-2xl px-3 py-2 text-sm italic shadow-sm ${
              message.is_mine
                ? "rounded-br-md bg-gray-200 text-gray-600 dark:bg-white/10 dark:text-gray-300"
                : "rounded-bl-md bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400"
            }`}
          >
            <p>{isArabic ? "تم حذف هذه الرسالة" : "This message was deleted"}</p>
          </div>
          <div className="flex items-center gap-1 px-1 text-[10px] text-gray-400">
            <span>{formatTime(message.created_at)}</span>
          </div>
        </div>
        {!message.is_mine && isArabic ? (
          <ChatSenderAvatar name={message.sender.name} url={message.sender.avatar_url} />
        ) : null}
      </div>
    );
  }

  return (
    <div
      className={`group flex w-full items-end gap-2 ${
        message.is_mine
          ? isArabic
            ? "justify-start"
            : "justify-end"
          : isArabic
            ? "justify-end"
            : "justify-start"
      }`}
    >
      {!message.is_mine && !isArabic ? (
        <ChatSenderAvatar name={message.sender.name} url={message.sender.avatar_url} />
      ) : null}
      <div className={`flex max-w-[85%] flex-col gap-1 ${align}`}>
      {!message.is_mine ? (
        <span className="px-1 text-xs font-medium text-gray-500 dark:text-gray-400">
          {message.sender.name ?? message.sender.role}
        </span>
      ) : null}
      <div className="relative">
        {showModerateDelete ? (
          <button
            type="button"
            onClick={() => onRequestDelete(message)}
            className={`absolute -top-2 z-10 rounded-full bg-white p-1 text-red-500 opacity-0 shadow-sm ring-1 ring-black/5 transition-opacity hover:bg-red-50 group-hover:opacity-100 dark:bg-navy-700 dark:ring-white/10 dark:hover:bg-red-500/20 ${
              message.is_mine ? (isArabic ? "left-0" : "right-0") : isArabic ? "right-0" : "left-0"
            }`}
            title={isArabic ? "حذف الرسالة" : "Delete message"}
            aria-label={isArabic ? "حذف الرسالة" : "Delete message"}
          >
            <MdDeleteOutline className="h-4 w-4" />
          </button>
        ) : null}
        <div
          className={`rounded-2xl px-3 py-2 text-sm shadow-sm ${roleBubbleClass(message.sender.role, message.is_mine)} ${
            message.is_mine ? "rounded-br-md" : "rounded-bl-md"
          } ${message.status === "failed" ? "ring-1 ring-red-400/60" : ""}`}
        >
        {message.message_type === "text" ? (
          <p className="whitespace-pre-wrap break-words">{message.body}</p>
        ) : null}

        {message.message_type === "image" && attachmentUrl ? (
          isLocal ? (
            <img src={attachmentUrl} alt="" className="max-h-56 rounded-lg object-cover" />
          ) : (
            <a href={attachmentUrl} target="_blank" rel="noreferrer">
              <img src={attachmentUrl} alt="" className="max-h-56 rounded-lg object-cover" />
            </a>
          )
        ) : null}

        {message.message_type === "file" && message.attachment ? (
          isPdf ? (
            <div className="flex w-60 max-w-full items-center gap-3 rounded-lg bg-black/5 p-2 dark:bg-white/5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-red-100 dark:bg-red-500/20">
                <MdPictureAsPdf className="h-6 w-6 text-red-500" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold">
                  {message.attachment.name ?? (isArabic ? "مستند PDF" : "PDF")}
                </p>
                <p className="text-[10px] opacity-70">
                  PDF{message.attachment.size ? ` · ${formatFileSize(message.attachment.size)}` : ""}
                </p>
                {!isLocal ? (
                  <div className="mt-1 flex items-center gap-3 text-[11px] font-semibold">
                    <button type="button" onClick={() => void openPdf()} className="underline">
                      {isArabic ? "معاينة" : "Preview"}
                    </button>
                    <button type="button" onClick={() => void downloadAttachment()} className="underline">
                      {isArabic ? "تحميل" : "Download"}
                    </button>
                  </div>
                ) : null}
              </div>
            </div>
          ) : isVideo ? (
            <div className="flex w-60 max-w-full items-center gap-3 rounded-lg bg-black/5 p-2 dark:bg-white/5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-brand-500/15">
                <MdVideocam className="h-6 w-6 text-brand-600 dark:text-brand-300" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold">
                  {message.attachment.name ?? (isArabic ? "فيديو" : "Video")}
                </p>
                <p className="text-[10px] opacity-70">
                  {fileExtensionLabel(message)}
                  {message.attachment.size ? ` · ${formatFileSize(message.attachment.size)}` : ""}
                </p>
                {!isLocal ? (
                  <div className="mt-1 flex items-center gap-3 text-[11px] font-semibold">
                    <button type="button" onClick={() => void openVideo()} className="underline">
                      {isArabic ? "تشغيل" : "Play"}
                    </button>
                    <button type="button" onClick={() => void downloadAttachment()} className="underline">
                      {isArabic ? "تحميل" : "Download"}
                    </button>
                  </div>
                ) : null}
              </div>
              {!isLocal ? (
                <button
                  type="button"
                  onClick={() => void openVideo()}
                  className="shrink-0 rounded-lg p-1.5 hover:bg-black/10 dark:hover:bg-white/10"
                  title={isArabic ? "تشغيل" : "Play"}
                >
                  <MdPlayArrow className="h-5 w-5" />
                </button>
              ) : null}
            </div>
          ) : (
            <div className="flex w-60 max-w-full items-center gap-3 rounded-lg bg-black/5 p-2 dark:bg-white/5">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-black/10 dark:bg-white/10">
                <MdInsertDriveFile className="h-6 w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold">
                  {message.attachment.name ?? (isArabic ? "ملف" : "File")}
                </p>
                <p className="text-[10px] opacity-70">
                  {fileExtensionLabel(message)}
                  {message.attachment.size ? ` · ${formatFileSize(message.attachment.size)}` : ""}
                </p>
              </div>
              {!isLocal ? (
                <button
                  type="button"
                  onClick={() => void downloadAttachment()}
                  className="shrink-0 rounded-lg p-1.5 hover:bg-black/10 dark:hover:bg-white/10"
                  title={isArabic ? "تحميل" : "Download"}
                >
                  <MdDownload className="h-5 w-5" />
                </button>
              ) : null}
            </div>
          )
        ) : null}

        {message.message_type === "voice" && attachmentUrl ? (
          <VoiceBubble
            url={attachmentUrl}
            durationSeconds={message.attachment?.duration_seconds}
            isArabic={isArabic}
            isMine={message.is_mine}
          />
        ) : null}

        {message.body && message.message_type !== "text" ? (
          <p className="mt-1 whitespace-pre-wrap break-words text-xs opacity-90">{message.body}</p>
        ) : null}

        {(message.status === "uploading" || message.status === "sending") &&
        message.upload_progress != null &&
        message.upload_progress < 100 ? (
          <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-black/10 dark:bg-white/10">
            <div className="h-full bg-current opacity-60" style={{ width: `${message.upload_progress}%` }} />
          </div>
        ) : null}
        </div>
      </div>

      <div className="flex items-center gap-1 px-1 text-[10px] text-gray-400">
        <StatusIndicator status={message.status} isArabic={isArabic} />
        <span>{formatTime(message.created_at)}</span>
        {message.status === "failed" ? (
          <span className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onRetry(message)}
              className="flex items-center gap-0.5 text-brand-500 hover:underline"
            >
              <MdRefresh className="h-3 w-3" />
              {isArabic ? "إعادة" : "Retry"}
            </button>
            <button
              type="button"
              onClick={() => onDeletePending(message)}
              className="text-red-400 hover:underline"
            >
              {isArabic ? "حذف" : "Delete"}
            </button>
          </span>
        ) : null}
      </div>

      {pdfOpen && attachmentUrl ? (
        <PdfPreviewModal
          url={attachmentUrl}
          name={message.attachment?.name ?? null}
          isArabic={isArabic}
          onClose={() => setPdfOpen(false)}
        />
      ) : null}
      {videoOpen && attachmentUrl ? (
        <ChatVideoPreviewModal
          url={attachmentUrl}
          name={message.attachment?.name ?? null}
          mimeType={message.attachment?.mime}
          isArabic={isArabic}
          onClose={() => setVideoOpen(false)}
        />
      ) : null}
      </div>
      {!message.is_mine && isArabic ? (
        <ChatSenderAvatar name={message.sender.name} url={message.sender.avatar_url} />
      ) : null}
    </div>
  );
}

export default function CourseChatPanel({
  courseId,
  scope,
  canMute = false,
  composerDisabled = false,
}: CourseChatPanelProps) {
  const { isArabic } = useDirection();
  // Best-effort uid known from a previous session, used for an instant cache read.
  const firebaseUidRef = React.useRef<string | null>(getRememberedFirebaseUid(scope));
  const snapshotArrivedRef = React.useRef(false);
  const initialCached = React.useMemo(
    () => loadCachedMessages(courseId, firebaseUidRef.current),
    [courseId]
  );
  const [realMessages, setRealMessages] = React.useState<CourseChatMessage[]>(initialCached);
  const [pending, setPending] = React.useState<CourseChatMessage[]>([]);
  const [settings, setSettings] = React.useState<CourseChatSettings | null>(null);
  const [notificationsMuted, setNotificationsMuted] = React.useState(false);
  const [notificationsMuteBusy, setNotificationsMuteBusy] = React.useState(false);
  // If we already have cached messages, don't block the UI with a skeleton.
  const [loading, setLoading] = React.useState(initialCached.length === 0);
  const [error, setError] = React.useState<string | null>(null);
  const [text, setText] = React.useState("");
  const [recording, setRecording] = React.useState(false);
  const [recordSeconds, setRecordSeconds] = React.useState(0);

  const listRef = React.useRef<HTMLDivElement | null>(null);
  const nearBottomRef = React.useRef(true);
  // Ensures we jump to the newest message the first time messages render.
  const didInitialScrollRef = React.useRef(false);
  const mediaRecorderRef = React.useRef<MediaRecorder | null>(null);
  const recordChunksRef = React.useRef<Blob[]>([]);
  const recordTimerRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const recordSecondsRef = React.useRef(0);
  const imageInputRef = React.useRef<HTMLInputElement | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  const studentMuted = scope === "student" && settings?.is_muted;
  // Optimistic UI: the composer stays usable; only individual messages carry status.
  const composerBlocked = composerDisabled || studentMuted || false;

  const messages = React.useMemo(() => mergeChatMessages(realMessages, pending), [realMessages, pending]);

  const connectionLabel = (mode: FirebaseConnectionMode): string => {
    if (mode === "live") return isArabic ? "مباشر" : "Live";
    if (mode === "connecting") return isArabic ? "جاري الاتصال" : "Connecting";
    return isArabic ? "تعذّر الاتصال" : "Offline";
  };

  const { connectionMode } = useFirebaseCourseChat(
    courseId,
    scope,
    {
      onMessages: (next) => {
        snapshotArrivedRef.current = true;
        setRealMessages(next);
        setLoading(false);
        setError(null);
        // Persist the latest server messages for the next instant open.
        saveCachedMessages(courseId, firebaseUidRef.current, next);
      },
      onSettings: setSettings,
      onBootstrap: (bootstrap) => {
        setNotificationsMuted(bootstrap.notifications_muted === true);
      },
      onIdentity: ({ firebaseUid }) => {
        firebaseUidRef.current = firebaseUid;
        rememberFirebaseUid(scope, firebaseUid);
      },
    },
    true
  );

  // Reconcile: drop optimistic messages that have arrived from Firestore.
  React.useEffect(() => {
    if (pending.length === 0) return;
    const realClientIds = new Set(
      realMessages.map((m) => m.client_message_id).filter((id): id is string => Boolean(id))
    );
    setPending((prev) =>
      prev.filter((p) => p.status === "failed" || !p.client_message_id || !realClientIds.has(p.client_message_id))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [realMessages]);

  // Best-effort web push registration (no-op unless a VAPID key is configured).
  React.useEffect(() => {
    void registerWebPush().catch(() => {});
  }, []);

  // Presence heartbeat only while the tab is visible (so FCM resumes when hidden).
  React.useEffect(() => {
    if (courseId == null || courseId === "") return;

    let interval: ReturnType<typeof setInterval> | null = null;

    const clearBeat = () => {
      if (interval != null) {
        clearInterval(interval);
        interval = null;
      }
    };

    const beat = () => {
      void courseChatService.heartbeat(courseId, true).catch(() => {});
    };

    const leave = () => {
      clearBeat();
      setActiveChatCourseId(null);
      void courseChatService.leaveChat(courseId).catch(() => {});
    };

    const start = () => {
      if (document.visibilityState !== "visible") return;
      clearBeat();
      setActiveChatCourseId(courseId);
      beat();
      interval = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        start();
      } else {
        leave();
      }
    };

    start();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", leave);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", leave);
      leave();
    };
  }, [courseId]);

  React.useEffect(() => {
    if (connectionMode === "error" && loading) {
      setLoading(false);
      setError(isArabic ? "تعذّر الاتصال بالمحادثة" : "Failed to connect to chat");
    }
  }, [connectionMode, isArabic, loading]);

  const scrollToBottom = React.useCallback((smooth = false) => {
    const el = listRef.current;
    if (!el) return;
    // Double rAF so the DOM has laid out the new rows before we measure.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const target = Math.max(0, el.scrollHeight - el.clientHeight);
        if (!smooth) {
          el.scrollTop = target;
          return;
        }
        const start = el.scrollTop;
        const distance = target - start;
        if (distance <= 2) {
          el.scrollTop = target;
          return;
        }
        const durationMs = 560;
        const t0 = performance.now();
        const easeOutCubic = (t: number) => 1 - (1 - t) ** 3;
        const step = (now: number) => {
          const p = Math.min(1, (now - t0) / durationMs);
          el.scrollTop = start + distance * easeOutCubic(p);
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    });
  }, []);

  // Open / refresh: always land on the newest message once content is ready.
  React.useEffect(() => {
    if (loading || messages.length === 0) return;
    if (didInitialScrollRef.current) {
      if (nearBottomRef.current) scrollToBottom(false);
      return;
    }

    let cancelled = false;
    let tries = 0;
    const maxTries = 20;

    const attempt = () => {
      if (cancelled) return;
      const el = listRef.current;
      tries += 1;
      if (!el) {
        if (tries < maxTries) window.setTimeout(attempt, 40);
        return;
      }

      // Wait until layout has a real scrollable height (or content fits).
      if (el.scrollHeight <= el.clientHeight + 4 && tries < 8) {
        window.setTimeout(attempt, 40);
        return;
      }

      didInitialScrollRef.current = true;
      nearBottomRef.current = true;
      scrollToBottom(true);
      // Snap again after animation / late image layout.
      window.setTimeout(() => {
        if (!cancelled) scrollToBottom(false);
      }, 620);
      window.setTimeout(() => {
        if (!cancelled) scrollToBottom(false);
      }, 1100);
    };

    attempt();
    return () => {
      cancelled = true;
    };
  }, [loading, messages.length, scrollToBottom]);

  // Reset the initial-jump when switching course/scope so reopening lands at the end.
  React.useEffect(() => {
    didInitialScrollRef.current = false;
    nearBottomRef.current = true;
  }, [courseId, scope]);

  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    nearBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };

  const buildOptimistic = (
    partial: Pick<CourseChatMessage, "message_type" | "body" | "attachment"> & {
      clientId: string;
      status: CourseChatMessage["status"];
      localPreviewUrl?: string | null;
    }
  ): CourseChatMessage => {
    const now = Date.now();
    return {
      id: partial.clientId,
      course_id: Number(courseId),
      sender: { id: 0, name: null, role: scopeRole(scope), avatar_url: null },
      message_type: partial.message_type,
      body: partial.body,
      attachment: partial.attachment,
      client_message_id: partial.clientId,
      is_mine: true,
      read_count: null,
      created_at: new Date(now).toISOString(),
      created_at_millis: now,
      created_at_human: null,
      is_deleted: false,
      status: partial.status,
      local: true,
      local_created_at_millis: now,
      local_preview_url: partial.localPreviewUrl ?? null,
      upload_progress: partial.status === "uploading" ? 0 : undefined,
    };
  };

  const updatePending = (clientId: string, patch: Partial<CourseChatMessage>) => {
    setPending((prev) => prev.map((p) => (p.client_message_id === clientId ? { ...p, ...patch } : p)));
  };

  const sendText = async () => {
    const body = text.trim();
    if (!body || composerBlocked) return;
    const clientId = makeClientMessageId();
    setPending((prev) => [
      ...prev,
      buildOptimistic({ clientId, status: "sending", message_type: "text", body, attachment: null }),
    ]);
    setText("");
    nearBottomRef.current = true;
    try {
      await courseChatService.sendText(scope, courseId, body, clientId);
      updatePending(clientId, { status: "sent" });
    } catch {
      updatePending(clientId, { status: "failed" });
    }
  };

  const sendFile = async (file: File, type: "image" | "file") => {
    if (composerBlocked) return;
    const clientId = makeClientMessageId();
    const previewUrl = type === "image" ? URL.createObjectURL(file) : null;
    setPending((prev) => [
      ...prev,
      buildOptimistic({
        clientId,
        status: "uploading",
        message_type: type,
        body: null,
        attachment: {
          url: null,
          name: file.name,
          mime: file.type || null,
          size: file.size,
          duration_seconds: null,
        },
        localPreviewUrl: previewUrl,
      }),
    ]);
    nearBottomRef.current = true;
    try {
      await courseChatService.sendAttachment(scope, courseId, type, file, {
        clientMessageId: clientId,
        onUploadProgress: (percent) =>
          updatePending(clientId, {
            upload_progress: percent,
            status: percent >= 100 ? "sending" : "uploading",
          }),
      });
      updatePending(clientId, { status: "sent" });
    } catch {
      updatePending(clientId, { status: "failed" });
    }
  };

  const startRecording = async () => {
    if (composerBlocked || !navigator.mediaDevices?.getUserMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      recordChunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) recordChunksRef.current.push(e.data);
      };
      recorder.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(recordChunksRef.current, { type: recorder.mimeType || "audio/webm" });
        if (blob.size === 0) return;
        const duration = recordSecondsRef.current;
        const clientId = makeClientMessageId();
        const previewUrl = URL.createObjectURL(blob);
        setPending((prev) => [
          ...prev,
          buildOptimistic({
            clientId,
            status: "uploading",
            message_type: "voice",
            body: null,
            attachment: { url: null, name: "voice.webm", mime: blob.type, size: blob.size, duration_seconds: duration },
            localPreviewUrl: previewUrl,
          }),
        ]);
        nearBottomRef.current = true;
        try {
          await courseChatService.sendAttachment(scope, courseId, "voice", blob, {
            voice_duration_seconds: duration,
            fileName: "voice.webm",
            clientMessageId: clientId,
            onUploadProgress: (percent) =>
              updatePending(clientId, {
                upload_progress: percent,
                status: percent >= 100 ? "sending" : "uploading",
              }),
          });
          updatePending(clientId, { status: "sent" });
        } catch {
          updatePending(clientId, { status: "failed" });
        }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setRecordSeconds(0);
      recordSecondsRef.current = 0;
      recordTimerRef.current = setInterval(() => {
        recordSecondsRef.current += 1;
        setRecordSeconds(recordSecondsRef.current);
      }, 1000);
    } catch {
      toastError(isArabic ? "تعذّر الوصول للميكروفون" : "Microphone access denied");
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    mediaRecorderRef.current = null;
    setRecording(false);
    if (recordTimerRef.current) clearInterval(recordTimerRef.current);
  };

  const retryMessage = async (m: CourseChatMessage) => {
    if (!m.client_message_id) return;
    // Only text retry can be re-sent without the original file in hand.
    if (m.message_type === "text" && m.body) {
      updatePending(m.client_message_id, { status: "sending" });
      try {
        await courseChatService.sendText(scope, courseId, m.body, m.client_message_id);
        updatePending(m.client_message_id, { status: "sent" });
      } catch {
        updatePending(m.client_message_id, { status: "failed" });
      }
      return;
    }
    toastError(isArabic ? "أعد إرفاق الملف وحاول مرة أخرى" : "Please re-attach the file and try again");
  };

  const deletePending = (m: CourseChatMessage) => {
    if (!m.client_message_id) return;
    if (m.local_preview_url) URL.revokeObjectURL(m.local_preview_url);
    setPending((prev) => prev.filter((p) => p.client_message_id !== m.client_message_id));
  };

  const canModerateDelete = scope === "instructor" || scope === "admin";
  const [deleteTarget, setDeleteTarget] = React.useState<CourseChatMessage | null>(null);

  const confirmDeleteMessage = async () => {
    if (!deleteTarget) return;
    try {
      await courseChatService.deleteMessage(scope, courseId, deleteTarget.id);
      invalidateAttachmentUrl(scope, courseId, deleteTarget.id);
      toastSuccess(isArabic ? "تم حذف الرسالة" : "Message deleted");
      // Firestore listener will patch is_deleted; no optimistic local removal.
    } catch (err) {
      toastError(getChatApiErrorMessage(err, isArabic));
      throw err;
    }
  };

  const toggleMute = async () => {
    if (!canMute) return;
    try {
      const next = settings?.is_muted
        ? await courseChatService.unmute(courseId)
        : await courseChatService.mute(courseId);
      if (next) setSettings(next);
    } catch {
      toastError(isArabic ? "تعذّر تحديث الكتم" : "Failed to update mute");
    }
  };

  const canMuteNotifications = scope === "student" || scope === "instructor";

  const toggleNotificationsMute = async () => {
    if (!canMuteNotifications || notificationsMuteBusy) return;
    const next = !notificationsMuted;
    setNotificationsMuteBusy(true);
    setNotificationsMuted(next);
    try {
      const result = await courseChatService.setNotificationsMuted(scope, courseId, next);
      setNotificationsMuted(result.notifications_muted === true);
    } catch {
      setNotificationsMuted(!next);
      toastError(isArabic ? "تعذّر تحديث كتم الإشعارات" : "Failed to update notification mute");
    } finally {
      setNotificationsMuteBusy(false);
    }
  };

  const retry = async () => {
    setLoading(true);
    setError(null);
    try {
      await courseChatService.bootstrap(scope, courseId);
    } catch (err) {
      setError(getChatApiErrorMessage(err, isArabic));
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100dvh-12rem)] min-h-[560px] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-navy-800">
      <div className="flex shrink-0 items-center justify-between border-b border-gray-100 px-4 py-3 dark:border-white/10">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-bold text-gray-800 dark:text-white">
            {isArabic ? "المحادثة" : "Chat"}
          </h2>
          <span
            className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
              connectionMode === "live"
                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-200"
                : connectionMode === "connecting"
                  ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-200"
                  : "bg-gray-100 text-gray-500 dark:bg-white/10 dark:text-gray-400"
            }`}
            title={connectionLabel(connectionMode)}
          >
            {connectionLabel(connectionMode)}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canMuteNotifications ? (
            <button
              type="button"
              onClick={() => void toggleNotificationsMute()}
              disabled={notificationsMuteBusy}
              title={
                notificationsMuted
                  ? isArabic
                    ? "إلغاء كتم الإشعارات"
                    : "Unmute notifications"
                  : isArabic
                    ? "كتم الإشعارات"
                    : "Mute notifications"
              }
              className="inline-flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-white/10 dark:text-white"
            >
              {notificationsMuted ? <MdNotificationsOff size={14} /> : <MdNotifications size={14} />}
              {notificationsMuted
                ? isArabic
                  ? "إلغاء كتم الإشعارات"
                  : "Unmute notifications"
                : isArabic
                  ? "كتم الإشعارات"
                  : "Mute notifications"}
            </button>
          ) : null}
          {canMute ? (
            <button
              type="button"
              onClick={() => void toggleMute()}
              className="rounded-lg bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-200 dark:bg-white/10 dark:text-white"
            >
              {settings?.is_muted
                ? isArabic
                  ? "إلغاء كتم الشات"
                  : "Unmute chat"
                : isArabic
                  ? "كتم الشات"
                  : "Mute chat"}
            </button>
          ) : null}
        </div>
      </div>

      {settings?.is_muted ? (
        <div className="shrink-0 bg-amber-50 px-4 py-2 text-xs text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
          {scope === "student"
            ? isArabic
              ? "تم إيقاف إرسال الرسائل في هذا الكورس بواسطة المحاضر"
              : "Sending messages is disabled by the instructor"
            : isArabic
              ? "الشات مكتوم. يمكن للطلاب القراءة فقط."
              : "Chat is muted. Students can read only."}
        </div>
      ) : null}

      <div className="course-chat-wallpaper relative min-h-0 flex-1">
        <div
          ref={listRef}
          onScroll={onScroll}
          className="absolute inset-0 space-y-3 overflow-y-auto px-4 py-3"
        >
        {loading ? (
          <div className="space-y-3">
            <Skeleton className="h-12 w-2/3" />
            <Skeleton className="h-12 w-1/2 ms-auto" />
            <Skeleton className="h-12 w-3/5" />
          </div>
        ) : null}

        {!loading && error ? (
          <div className="flex flex-col items-center gap-2 py-8 text-center text-sm text-red-500">
            <p>{error}</p>
            <button type="button" onClick={() => void retry()} className="text-brand-500 underline">
              {isArabic ? "إعادة المحاولة" : "Retry"}
            </button>
          </div>
        ) : null}

        {!loading && !error && messages.length === 0 ? (
          <p className="py-10 text-center text-sm text-gray-400">
            {isArabic ? "لا توجد رسائل بعد" : "No messages yet"}
          </p>
        ) : null}

        {messages.map((m) => (
          <MessageBubble
            key={m.client_message_id ?? m.id}
            message={m}
            isArabic={isArabic}
            scope={scope}
            courseId={courseId}
            canModerateDelete={canModerateDelete}
            onRetry={retryMessage}
            onDeletePending={deletePending}
            onRequestDelete={setDeleteTarget}
          />
        ))}
      </div>
      </div>

      <ConfirmDialog
        open={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDeleteMessage}
        title={isArabic ? "حذف الرسالة" : "Delete message"}
        description={isArabic ? "هل تريد حذف هذه الرسالة؟" : "Delete this message?"}
        confirmLabel={isArabic ? "حذف" : "Delete"}
        variant="danger"
      />

      <div className="shrink-0 border-t border-gray-100 p-3 dark:border-white/10">
        {recording ? (
          <div className="flex items-center justify-between gap-2 rounded-xl bg-red-50 px-3 py-2 dark:bg-red-500/10">
            <span className="text-sm text-red-600 dark:text-red-200">
              {isArabic ? "جاري التسجيل" : "Recording"} {recordSeconds}s
            </span>
            <button type="button" onClick={stopRecording} className="rounded-lg bg-red-500 p-2 text-white">
              <MdStop className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <div className="flex items-end gap-2">
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void sendFile(f, "image");
                e.target.value = "";
              }}
            />
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void sendFile(f, "file");
                e.target.value = "";
              }}
            />
            <button
              type="button"
              disabled={composerBlocked}
              onClick={() => imageInputRef.current?.click()}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-white/10"
              title={isArabic ? "صورة" : "Image"}
            >
              <MdImage className="h-5 w-5" />
            </button>
            <button
              type="button"
              disabled={composerBlocked}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-white/10"
              title={isArabic ? "ملف" : "File"}
            >
              <MdAttachFile className="h-5 w-5" />
            </button>
            <button
              type="button"
              disabled={composerBlocked}
              onClick={() => void startRecording()}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-white/10"
              title={isArabic ? "تسجيل صوت" : "Voice"}
            >
              <MdMic className="h-5 w-5" />
            </button>
            <textarea
              value={text}
              disabled={composerBlocked}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  void sendText();
                }
              }}
              rows={1}
              placeholder={isArabic ? "اكتب رسالة..." : "Type a message..."}
              className="max-h-28 min-h-[42px] flex-1 resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-navy-900 dark:text-white"
            />
            <button
              type="button"
              disabled={composerBlocked || !text.trim()}
              onClick={() => void sendText()}
              className="rounded-xl bg-brand-500 p-2.5 text-white hover:bg-brand-600 disabled:opacity-40"
            >
              <MdSend className={`h-5 w-5 ${isArabic ? "rotate-180" : ""}`} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
